import React, { useState, useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import UseAuth from '../hooks/UseAuth';
import UseAxiosSecure from '../hooks/UseAxiosSecure';

const BACKEND_URL = 'https://chatbot.getsoko.app';
// const BACKEND_URL = 'http://localhost:8080'; // For local development

const Dashboard = () => {
  const { user, logOutUser } = UseAuth();
  const axiosSecure = UseAxiosSecure();

  const [activeTab, setActiveTab] = useState('chat');
  const [sessions, setSessions] = useState([]);
  const [activeTicket, setActiveTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  
  // Mobile UI State
  const [isMobileChatOpen, setIsMobileChatOpen] = useState(false);

  // Admin States
  const [team, setTeam] = useState([]);
  const [formData, setFormData] = useState({ name: '', email: '', password: '', role: 'agent' });
  const [adminStatus, setAdminStatus] = useState({ type: '', msg: '' });

  // WebRTC States
  const [isCalling, setIsCalling] = useState(false);
  const [callStatus, setCallStatus] = useState('');

  // Refs for stable Socket & State
  const socketRef = useRef(null);
  const messagesEndRef = useRef(null);
  const activeTicketRef = useRef(null); 
  
  // WebRTC Refs
  const localStreamRef = useRef(null);
  const peerConnectionRef = useRef(null);
  const remoteAudioRef = useRef(null);

  const rtcConfig = {
      iceServers: [
          { urls: 'stun:stun.l.google.com:19302' },
          { urls: 'stun:stun1.l.google.com:19302' }
      ]
  };

  const playAlert = () => {
    try {
      const audio = new Audio('/alert.mp3');
      audio.play();
      const originalTitle = document.title;
      document.title = "(1) INCOMING - SYSTEM";
      setTimeout(() => { document.title = originalTitle; }, 4000);
    } catch (err) {}
  };

  // Keep ref synced with state so Socket always knows what ticket we are on
  useEffect(() => {
    activeTicketRef.current = activeTicket;
  }, [activeTicket]);

  // 1. MASTER SOCKET CONNECTION (Connects ONCE, never drops messages)
  useEffect(() => {
    const safeAgentId = user?._id || user?.id;
    if (!safeAgentId) return; 
    
    const socket = io(BACKEND_URL, { auth: { agentId: safeAgentId } });
    socketRef.current = socket;

    const fetchSessions = async () => {
      try {
        const timestamp = new Date().getTime();
        const res = await axiosSecure.get(`/chat/sessions/queued?_t=${timestamp}`);
        if (res.data.success) {
            setSessions(res.data.data);
        }
      } catch (err) {}
    };
    
    fetchSessions();

    socket.on('refresh_queue', () => { 
        fetchSessions(); 
        playAlert(); 
    });

    socket.on('receive_message', (msg) => {
        if (msg.senderId !== safeAgentId) {
            playAlert();
            if (msg.sessionId === activeTicketRef.current?._id) {
                setMessages((prev) => [...prev, msg]);
            }
        }
    });

    // ⚡️ WEBRTC GLOBAL LISTENERS
    socket.on('call_answered', async (data) => {
        if (peerConnectionRef.current) {
            await peerConnectionRef.current.setRemoteDescription(new RTCSessionDescription(data.answer));
            setCallStatus('SECURE LINE ACTIVE 🎙️');
        }
    });
    
    socket.on('ice_candidate', async (data) => {
        if (peerConnectionRef.current) await peerConnectionRef.current.addIceCandidate(new RTCIceCandidate(data.candidate));
    });
    
    socket.on('call_ended', () => stopCall());

    return () => socket.disconnect();
  }, [user?._id, user?.id]); 

  // 2. FETCH HISTORY WHEN CLICKING A TICKET
  useEffect(() => {
    if (!activeTicket?._id || !socketRef.current) return; 
    const fetchHistory = async () => {
      try {
        const res = await axiosSecure.get(`/chat/session/${activeTicket._id}/messages`);
        if (res.data.success) setMessages(res.data.data);
      } catch (err) {}
    };
    fetchHistory();
    socketRef.current.emit('join_session', activeTicket._id);
  }, [activeTicket?._id, axiosSecure]); 

  // 3. AUTO-LOAD AGENT LIST IN ADMIN MODE
  useEffect(() => {
      if (activeTab === 'admin' && user?.role === 'admin') {
          const fetchTeam = async () => {
              try {
                  const res = await axiosSecure.get('/auth/agents');
                  if (res.data.success) setTeam(res.data.data);
              } catch (err) {}
          };
          fetchTeam();
      }
  }, [activeTab, user?.role, axiosSecure]);

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Actions
  const sendMessage = async () => {
    if (!inputValue.trim() || !activeTicket) return;
    
    const safeAgentId = user?._id || user?.id || 'unknown_agent';
    const safeAgentName = user?.name || 'Live Agent';

    const msgData = { 
        sessionId: activeTicket._id, 
        senderType: 'agent', 
        senderId: safeAgentId, 
        content: inputValue 
    };
    
    socketRef.current.emit('send_message', msgData);
    setMessages((prev) => [...prev, msgData]);
    setInputValue('');

    if (activeTicket.status === 'queued') {
      try {
        await axiosSecure.patch(`/chat/session/${activeTicket._id}/status`, { 
            status: 'active', 
            agentId: safeAgentId, 
            agentName: safeAgentName 
        });
        setActiveTicket((prev) => ({ ...prev, status: 'active' }));
        setSessions((prev) => prev.map((t) => t._id === activeTicket._id ? { ...t, status: 'active' } : t));
      } catch (err) {}
    }
  };

  const stopCall = () => {
      if (peerConnectionRef.current) peerConnectionRef.current.close();
      if (localStreamRef.current) localStreamRef.current.getTracks().forEach(track => track.stop());
      peerConnectionRef.current = null;
      localStreamRef.current = null;
      setIsCalling(false);
      setCallStatus('');
  };

  const startCall = async () => {
      if (!activeTicket) return;
      try {
          setCallStatus('INITIALIZING...');
          const stream = await navigator.mediaDevices.getUserMedia({ 
              audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, sampleRate: 48000 } 
          });
          localStreamRef.current = stream;
          setIsCalling(true);
          setCallStatus('CALLING TARGET...');

          const pc = new RTCPeerConnection(rtcConfig);
          peerConnectionRef.current = pc;
          stream.getTracks().forEach(track => pc.addTrack(track, stream));

          pc.ontrack = (event) => { if (remoteAudioRef.current) remoteAudioRef.current.srcObject = event.streams[0]; };
          pc.onicecandidate = (event) => { if (event.candidate) socketRef.current.emit('ice_candidate', { sessionId: activeTicket._id, candidate: event.candidate }); };

          const offer = await pc.createOffer();
          await pc.setLocalDescription(offer);
          socketRef.current.emit('call_user', { sessionId: activeTicket._id, offer: offer });
      } catch (error) {
          alert("Microphone required for voice link.");
          stopCall();
      }
  };

  const resolveTicket = async () => {
    if (!activeTicket) return;
    try {
      stopCall(); 
      await axiosSecure.patch(`/chat/session/${activeTicket._id}/status`, { status: 'closed' });
      socketRef.current.emit('close_session', activeTicket._id);
      setSessions((prev) => prev.filter((t) => t._id !== activeTicket._id));
      setActiveTicket(null);
      setIsMobileChatOpen(false);
    } catch (err) {}
  };

  const handleTicketSelect = (ticket) => {
      setActiveTicket(ticket);
      setIsMobileChatOpen(true);
  };

  const handleCreateAgent = async (e) => {
    e.preventDefault();
    setAdminStatus({ type: '', msg: '' });
    try {
        const res = await axiosSecure.post('/auth/register', formData);
        if (res.data.success) {
            setAdminStatus({ type: 'success', msg: `Agent ${formData.name} online.` });
            setFormData({ name: '', email: '', password: '', role: 'agent' });
            const teamRes = await axiosSecure.get('/auth/agents');
            if (teamRes.data.success) setTeam(teamRes.data.data);
        }
    } catch (err) {
        setAdminStatus({ type: 'error', msg: 'System fault: Agent creation failed.' });
    }
  };

  return (
    <div className="flex flex-col h-screen bg-[#0a0f16] font-sans text-gray-200 overflow-hidden">
      <audio ref={remoteAudioRef} autoPlay className="hidden" />

      {/* TOP NAVIGATION BAR - HIGH TECH DARK MODE */}
      <div className="bg-[#111822] border-b border-[#1d2633] flex justify-between items-center px-4 md:px-6 py-3 shadow-[0_4_20px_rgba(0,0,0,0.5)] z-20 flex-shrink-0">
        <div className="flex items-center gap-4 md:gap-8">
            <h1 className="font-black text-xl md:text-2xl tracking-widest text-emerald-400 drop-shadow-[0_0_8px_rgba(16,185,129,0.3)]">
                SOKO<span className="text-gray-100">DESK</span>
            </h1>
            <div className="hidden md:flex bg-[#0a0f16] p-1 rounded-lg border border-[#1d2633]">
                <button onClick={() => setActiveTab('chat')} className={`px-4 py-1.5 rounded text-sm font-bold tracking-wide transition-all ${activeTab === 'chat' ? 'bg-emerald-500/10 text-emerald-400 shadow-inner' : 'text-gray-500 hover:text-gray-300'}`}>COMMS LINK</button>
                {user?.role === 'admin' && (
                    <button onClick={() => setActiveTab('admin')} className={`px-4 py-1.5 rounded text-sm font-bold tracking-wide transition-all ${activeTab === 'admin' ? 'bg-emerald-500/10 text-emerald-400 shadow-inner' : 'text-gray-500 hover:text-gray-300'}`}>SYSTEM DB</button>
                )}
            </div>
        </div>
        
        <div className="flex items-center gap-3 md:gap-4">
            <div className="hidden md:flex flex-col items-end mr-2">
                <span className="text-sm font-bold text-gray-200">{user?.name}</span>
                <span className="text-[10px] font-mono text-emerald-500 tracking-widest uppercase opacity-80">{user?.role}</span>
            </div>
            <div className="w-9 h-9 rounded bg-[#1d2633] border border-emerald-500/30 text-emerald-400 flex items-center justify-center font-bold shadow-[0_0_10px_rgba(16,185,129,0.1)]">
                {user?.name?.charAt(0).toUpperCase()}
            </div>
            <button onClick={logOutUser} className="hidden md:block text-gray-500 hover:text-red-500 transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"></path></svg>
            </button>
            <div className="md:hidden flex gap-2">
                {user?.role === 'admin' && (
                    <button onClick={() => setActiveTab(activeTab === 'chat' ? 'admin' : 'chat')} className="p-2 text-emerald-400 bg-[#1d2633] rounded">
                         <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"></path></svg>
                    </button>
                )}
            </div>
        </div>
      </div>

      {activeTab === 'admin' && user?.role === 'admin' ? (
          /* ================= ADMIN VIEW ================= */
          <div className="flex-1 overflow-y-auto p-4 md:p-8 flex flex-col md:flex-row gap-6 items-start bg-[#0a0f16]">
              <div className="w-full md:w-1/3 bg-[#111822] p-6 rounded-lg shadow-lg border border-[#1d2633]">
                  <h2 className="text-sm font-mono text-emerald-400 tracking-widest mb-5 border-b border-[#1d2633] pb-2">ADD OPERATOR</h2>
                  {adminStatus.msg && (
                      <div className={`p-3 rounded mb-4 text-xs font-mono ${adminStatus.type === 'success' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30' : 'bg-red-500/10 text-red-400 border border-red-500/30'}`}>{adminStatus.msg}</div>
                  )}
                  <form onSubmit={handleCreateAgent} className="space-y-4">
                      <div><label className="block text-[10px] font-mono text-gray-500 uppercase tracking-widest mb-1">Identity</label><input type="text" required value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="w-full bg-[#0a0f16] border border-[#1d2633] rounded px-4 py-2 outline-none focus:border-emerald-500 text-sm text-gray-200" /></div>
                      <div><label className="block text-[10px] font-mono text-gray-500 uppercase tracking-widest mb-1">Clearance Code (Email)</label><input type="email" required value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="w-full bg-[#0a0f16] border border-[#1d2633] rounded px-4 py-2 outline-none focus:border-emerald-500 text-sm text-gray-200" /></div>
                      <div><label className="block text-[10px] font-mono text-gray-500 uppercase tracking-widest mb-1">Access Key</label><input type="password" required value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})} className="w-full bg-[#0a0f16] border border-[#1d2633] rounded px-4 py-2 outline-none focus:border-emerald-500 text-sm text-gray-200" /></div>
                      <div>
                          <label className="block text-[10px] font-mono text-gray-500 uppercase tracking-widest mb-1">Security Level</label>
                          <select value={formData.role} onChange={(e) => setFormData({...formData, role: e.target.value})} className="w-full bg-[#0a0f16] border border-[#1d2633] rounded px-4 py-2 outline-none focus:border-emerald-500 text-sm text-gray-200">
                              <option value="agent">Support Operator</option>
                              <option value="admin">System Admin</option>
                          </select>
                      </div>
                      <button type="submit" className="w-full bg-emerald-500 hover:bg-emerald-400 text-[#0a0f16] font-bold font-mono tracking-widest py-3 rounded mt-4 transition-all shadow-[0_0_15px_rgba(16,185,129,0.3)] hover:shadow-[0_0_25px_rgba(16,185,129,0.5)] text-xs">INITIALIZE OPERATOR</button>
                  </form>
              </div>

              <div className="w-full md:w-2/3 bg-[#111822] p-6 rounded-lg shadow-lg border border-[#1d2633]">
                  <h2 className="text-sm font-mono text-emerald-400 tracking-widest mb-5 border-b border-[#1d2633] pb-2">OPERATOR DATABASE</h2>
                  <div className="grid gap-3">
                      {team.map((member) => (
                          <div key={member._id} className="flex flex-col sm:flex-row justify-between sm:items-center p-4 border border-[#1d2633] rounded bg-[#0a0f16] hover:border-emerald-500/50 transition-colors gap-4">
                              <div className="flex items-center gap-4">
                                  <div className="w-10 h-10 rounded bg-[#111822] border border-emerald-500/20 text-emerald-500 flex items-center justify-center font-bold text-sm shadow-inner">
                                      {member.name.charAt(0)}
                                  </div>
                                  <div>
                                      <p className="font-bold text-gray-200 text-sm flex items-center gap-2">
                                          {member.name} 
                                          <span className="text-[9px] font-mono uppercase tracking-widest px-2 py-0.5 rounded bg-[#1d2633] text-gray-400">{member.role}</span>
                                      </p>
                                      <p className="text-xs text-gray-500 mt-0.5">{member.email}</p>
                                  </div>
                              </div>
                              <div className="flex items-center justify-between sm:justify-end gap-6 border-t sm:border-0 border-[#1d2633] pt-3 sm:pt-0">
                                  <div className="text-left sm:text-center">
                                      <p className="text-[9px] font-mono text-gray-500 uppercase tracking-widest">Active Links</p>
                                      <p className="text-base font-black text-emerald-400 leading-tight">{member.activeTicketCount || 0}</p>
                                  </div>
                                  <div className={`px-3 py-1 rounded text-[9px] font-mono font-bold uppercase tracking-widest w-20 text-center border ${member.status === 'online' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30 shadow-[0_0_8px_rgba(16,185,129,0.1)]' : 'bg-[#1d2633] text-gray-500 border-transparent'}`}>
                                      {member.status}
                                  </div>
                              </div>
                          </div>
                      ))}
                  </div>
              </div>
          </div>
      ) : (
          /* ================= CHAT VIEW ================= */
          <div className="flex-1 flex overflow-hidden">
             
            {/* LEFT SIDEBAR: Queue */}
            <div className={`${isMobileChatOpen ? 'hidden md:flex' : 'flex'} w-full md:w-80 lg:w-96 bg-[#111822] border-r border-[#1d2633] flex-col flex-shrink-0 z-10`}>
                <div className="p-4 bg-[#0a0f16] border-b border-[#1d2633] flex justify-between items-center shadow-md">
                    <h2 className="text-xs font-mono font-bold text-emerald-400 tracking-widest">AWAITING CONNECTION</h2>
                    <span className="bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 px-2 py-0.5 rounded text-[10px] font-mono">{sessions.length}</span>
                </div>
                <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-[#0a0f16]">
                {sessions.length === 0 && (
                    <div className="text-center p-8 text-gray-600">
                        <svg className="w-10 h-10 mx-auto mb-3 opacity-20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M13 10V3L4 14h7v7l9-11h-7z"></path></svg>
                        <p className="text-xs font-mono tracking-widest uppercase">Grid is clear</p>
                    </div>
                )}
                {sessions.map((ticket) => (
                    <div key={ticket._id} onClick={() => handleTicketSelect(ticket)} className={`p-4 rounded cursor-pointer transition-all border ${activeTicket?._id === ticket._id ? 'border-emerald-500 bg-[#111822] shadow-[0_0_10px_rgba(16,185,129,0.1)]' : 'bg-[#111822] border-[#1d2633] hover:border-emerald-500/50'}`}>
                        <div className="flex justify-between items-start mb-2">
                            <span className="font-bold text-gray-200 text-sm truncate pr-2">{ticket.customerName}</span>
                            <span className={`text-[9px] font-mono uppercase tracking-widest px-2 py-1 rounded flex-shrink-0 border ${ticket.status === 'active' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' : 'bg-amber-500/10 text-amber-400 border-amber-500/30'}`}>
                                {ticket.status === 'queued' ? 'PENDING' : 'LINKED'}
                            </span>
                        </div>
                        <div className="flex items-center gap-2 text-[11px] font-mono text-gray-500">
                            <span className="truncate">ORIGIN // {ticket.originApp}</span>
                        </div>
                    </div>
                ))}
                </div>
            </div>

            {/* RIGHT MAIN AREA: Chat */}
            <div className={`${isMobileChatOpen ? 'flex' : 'hidden md:flex'} flex-1 flex-col bg-[#0a0f16] relative`}>
                {activeTicket ? (
                <>
                    {/* CHAT HEADER */}
                    <div className="p-3 md:p-4 bg-[#111822] border-b border-[#1d2633] flex justify-between items-center z-10 shadow-lg">
                        <div className="flex items-center gap-3">
                            <button onClick={() => setIsMobileChatOpen(false)} className="md:hidden p-2 -ml-2 text-gray-400 hover:text-emerald-400 rounded">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path></svg>
                            </button>
                            <div className="w-10 h-10 rounded bg-[#0a0f16] flex items-center justify-center text-emerald-500 border border-[#1d2633]">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M13 10V3L4 14h7v7l9-11h-7z"></path></svg>
                            </div>
                            <div>
                                <h2 className="font-bold text-sm md:text-base text-gray-100 leading-tight">{activeTicket.customerName}</h2>
                                <p className="text-[10px] font-mono text-gray-500 uppercase mt-0.5 tracking-wider">ID: {activeTicket.customerId}</p>
                            </div>
                        </div>
                        <div className="flex gap-2 items-center">
                            {isCalling && <span className="hidden sm:inline text-[10px] font-mono font-bold text-red-500 mr-2 animate-pulse">{callStatus}</span>}
                            {!isCalling ? (
                                <button onClick={startCall} className="flex items-center justify-center w-9 h-9 md:w-auto md:px-4 md:py-1.5 rounded md:rounded bg-emerald-500/10 hover:bg-emerald-500 text-emerald-400 hover:text-[#0a0f16] font-bold font-mono text-xs transition-all border border-emerald-500/30 hover:shadow-[0_0_15px_rgba(16,185,129,0.5)]">
                                    <svg className="w-4 h-4 md:mr-2" fill="currentColor" viewBox="0 0 20 20"><path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"></path></svg>
                                    <span className="hidden md:inline uppercase tracking-widest">Init Voice</span>
                                </button>
                            ) : (
                                <button onClick={() => { socketRef.current.emit('end_call', activeTicket._id); stopCall(); }} className="flex items-center justify-center w-9 h-9 md:w-auto md:px-4 md:py-1.5 rounded bg-red-500/10 hover:bg-red-500 text-red-400 hover:text-white font-bold font-mono text-xs transition-all border border-red-500/30 shadow-[0_0_10px_rgba(239,68,68,0.3)]">
                                    <span className="hidden md:inline uppercase tracking-widest">Abort Link</span>
                                    <span className="md:hidden">X</span>
                                </button>
                            )}

                            <button onClick={resolveTicket} className="text-[10px] font-mono uppercase tracking-widest bg-[#1d2633] hover:bg-[#2d3a4d] px-3 py-1.5 md:px-4 md:py-2 rounded text-gray-300 font-bold transition-all border border-[#2d3a4d]">
                                <span className="hidden md:inline">Terminate</span>
                                <span className="md:hidden">END</span>
                            </button>
                        </div>
                    </div>

                    {/* CHAT HISTORY */}
                    <div className="flex-1 overflow-y-auto p-4 md:p-6 bg-[#0a0f16] flex flex-col gap-4 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-[#0a0f16] to-[#05080b]">
                        {messages.length === 0 && (
                            <div className="m-auto text-center text-gray-600 text-xs font-mono tracking-widest">CONNECTION ESTABLISHED. WAITING FOR DATA.</div>
                        )}
                        {messages.map((msg, index) => (
                            <div key={index} className={`flex flex-col ${msg.senderType === 'agent' ? 'items-end' : 'items-start'}`}>
                                <div className={`px-4 py-2.5 rounded max-w-[85%] md:max-w-md text-sm shadow-sm font-sans ${msg.senderType === 'agent' ? 'bg-emerald-600/20 border border-emerald-500/30 text-emerald-50 rounded-tr-none shadow-[0_0_10px_rgba(16,185,129,0.05)]' : 'bg-[#111822] border border-[#1d2633] text-gray-300 rounded-tl-none'}`}>
                                    {msg.content}
                                </div>
                            </div>
                        ))}
                        <div ref={messagesEndRef} />
                    </div>

                    {/* MESSAGE INPUT */}
                    <div className="p-3 md:p-4 bg-[#111822] border-t border-[#1d2633] flex gap-2 md:gap-3">
                        <input type="text" value={inputValue} onChange={(e) => setInputValue(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && sendMessage()} placeholder="Transmit data..." className="flex-1 bg-[#0a0f16] border border-[#1d2633] rounded px-4 py-2.5 md:py-3 outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all text-sm text-gray-200 placeholder-gray-600 font-mono" />
                        <button onClick={sendMessage} className="bg-emerald-500 hover:bg-emerald-400 text-[#0a0f16] font-bold p-2.5 md:px-6 md:py-3 rounded transition-all shadow-[0_0_10px_rgba(16,185,129,0.2)] flex items-center justify-center font-mono tracking-widest text-xs uppercase">
                            <svg className="w-5 h-5 md:hidden" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"></path></svg>
                            <span className="hidden md:inline">Transmit</span>
                        </button>
                    </div>
                </>
                ) : (
                <div className="flex-1 flex flex-col items-center justify-center text-gray-500 bg-[#0a0f16]">
                    <div className="w-20 h-20 bg-[#111822] rounded-full flex items-center justify-center border border-[#1d2633] mb-4 shadow-[0_0_15px_rgba(0,0,0,0.5)]">
                        <svg className="w-10 h-10 text-emerald-500/50" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"></path></svg>
                    </div>
                    <p className="text-sm font-mono tracking-widest text-emerald-500 uppercase">System Ready</p>
                    <p className="text-[10px] font-mono mt-2 text-gray-600 uppercase">Select a node from the grid to establish a link.</p>
                </div>
                )}
            </div>
          </div>
      )}
    </div>
  );
};

export default Dashboard;